// BF collection, search and cart behaviour on top of native Shopify forms (every form also works without JavaScript).
(() => {
  const bf = window.BF = window.BF || {};
  if (bf.commerceBound) return;
  bf.commerceBound = true;
  const pendingAdds = bf.pendingAdds = bf.pendingAdds || new WeakSet();
  const root = window.Shopify?.routes?.root || '/';
  const getJSON = (el, fallback) => {
    try { return JSON.parse(el?.textContent || ''); } catch { return fallback; }
  };

  // Formats minor units with the shop's money format ({{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}} …).
  const formatMoney = (cents, format) => {
    const value = (cents / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const group = (sep) => int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const map = {
      amount: `${group(',')}.${dec}`,
      amount_no_decimals: group(','),
      amount_with_comma_separator: `${group('.')},${dec}`,
      amount_no_decimals_with_comma_separator: group('.'),
      amount_with_apostrophe_separator: `${group("'")}.${dec}`,
      amount_with_period_and_space_separator: `${group(' ')}.${dec}`,
    };
    return String(format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => map[key] ?? map.amount);
  };
  window.BF = Object.assign(window.BF || {}, { formatMoney });

  // Shared polite announcer for actions outside a section's own live region.
  const announce = (text) => {
    let region = document.getElementById('bf-announcer');
    if (!region) {
      region = document.createElement('p');
      region.id = 'bf-announcer';
      region.className = 'bf-sr';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.append(region);
    }
    region.textContent = '';
    setTimeout(() => { region.textContent = text; }, 50);
  };

  // Product cards: add one unit through /cart/add.js, keep the header count in sync.
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest?.('[data-bf-card-add]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('button');
    if (button?.disabled || pendingAdds.has(form)) return;
    pendingAdds.add(form);
    if (button) button.disabled = true;
    button?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message);
      announce((window.BF_T?.added || '%title%').replace('%title%', data.product_title || data.title));
      button.dataset.bfAdded = 'true';
      try {
        const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (cartResponse.ok) window.BF?.announceCart?.((await cartResponse.json()).item_count);
      } catch { /* addition succeeded; do not invite a duplicate retry */ }
      window.BF?.refresh?.();
    } catch (error) {
      announce(error.message || window.BF_T?.addError || '');
    } finally {
      button?.removeAttribute('aria-busy');
      pendingAdds.delete(form);
      if (button) button.disabled = false;
    }
  });

  // Collection filters: apply on change; the filter panel starts closed on small screens.
  document.querySelectorAll('[data-bf-filters]').forEach((details) => {
    const form = details.querySelector('[data-bf-filter-form]');
    form?.addEventListener('change', (event) => {
      if (event.target.matches('input[type="checkbox"]')) form.requestSubmit();
    });
  });
  document.addEventListener('change', (event) => {
    if (event.target.matches?.('[data-bf-autosubmit]')) event.target.form?.requestSubmit();
  });
  // Native details controls retain no-JS operation; keep only one filter popover open and restore focus on Escape.
  document.addEventListener('toggle', (event) => {
    if (!event.target.matches?.('.bf-plp__dropdown[open]')) return;
    document.querySelectorAll('.bf-plp__dropdown[open]').forEach((el) => { if (el !== event.target) el.open = false; });
  }, true);
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('.bf-plp__dropdown[open]');
    if (open) { open.open = false; open.querySelector('summary')?.focus(); }
  });
  document.addEventListener('click', (event) => {
    document.querySelectorAll('.bf-plp__dropdown[open]').forEach((el) => { if (!el.contains(event.target)) el.open = false; });
  });

  // Cart.
  // A quantity change shows at once: the quantity, the offer progress, the header count and, while no discount can change
  // them, the arithmetic line and order totals (marked as updating). Shopify then confirms the change with one
  // /cart/update.js request that also returns the re-rendered section (bundled section rendering), so the prices, discounts
  // and totals left on screen are always Shopify's own. One request runs at a time; changes made meanwhile wait and go out
  // together in the next request, and a response is drawn only when no newer change is waiting, so an older response never
  // overwrites a newer quantity. If a request fails, the cart is redrawn from Shopify and an error is shown.
  const cartRoot = () => document.querySelector('[data-bf-cart-section]');
  const sectionIdOf = (section) => section?.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '');
  const cartData = (section) => ({
    lines: getJSON(section?.querySelector('[data-bf-cart-lines]'), []),
    t: getJSON(section?.querySelector('[data-bf-cart-i18n]'), {}),
  });
  const cartState = { desired: new Map(), inFlight: false, timer: 0, since: 0, count: null };
  const COALESCE_MS = 150; // merges double and triple taps into one request
  const MAX_WAIT_MS = 600; // but never holds a change back longer than this
  const TIMEOUT_MS = 15000;

  // Same bucket keys as the Liquid section (bf-promo-bucket): oil by oil type and exact size, perfume by exact variant,
  // other groups by name. bucketKey() in bf-promo-engine.js is the single JavaScript implementation.
  const bucketOf = (line) => (window.BFPromo ? window.BFPromo.bucketKey(line) : line.bucket) || 'none';
  const localLines = (lines) => lines.map((l) => ({ ...l, quantity: cartState.desired.has(l.id) ? cartState.desired.get(l.id) : l.quantity }));
  const fill = (text, values) => Object.entries(values).reduce((s, [k, v]) => s.split(`%${k}%`).join(String(v)), text || '');

  const markLines = (lines) => {
    const section = cartRoot();
    if (!section || !window.BFPromo) return;
    const { t } = cartData(section);
    lines = lines || cartData(section).lines;
    const result = window.BFPromo.evaluate(lines.filter((l) => l.quantity > 0));
    const freeByLine = new Map();
    result.groups.forEach((g) => g.freeUnits.forEach((u) => freeByLine.set(u.id, u.quantity)));
    // Lines as Shopify last rendered them, with the units Shopify itself made free ("allocated").
    const server = new Map(cartData(section).lines.map((l) => [l.id, l]));
    section.querySelectorAll('[data-bf-mark]').forEach((el) => {
      const line = lines.find((l) => l.id === el.dataset.bfMark);
      const free = freeByLine.get(el.dataset.bfMark) || 0;
      delete el.dataset.bfMismatch;
      if (!line || !line.group || line.eligible === false || line.quantity === 0) { el.textContent = ''; return; }
      if (!t.live) {
        // Preview: the rule is explained, no money is claimed.
        el.textContent = free > 0 ? t.freePreview.replace('%count%', free) : t.eligible;
        return;
      }
      // Live: "free" only from Shopify's allocation. While a quantity change is still on its way to Shopify, nothing
      // is claimed; the confirmed section render brings the new allocation.
      const shown = server.get(line.id);
      const pending = !shown || shown.quantity !== line.quantity;
      const allocated = pending ? 0 : Number(shown.allocated) || 0;
      el.textContent = allocated > 0 ? t.freeLive.replace('%count%', allocated) : t.eligible;
      if (!pending && free > 0 && allocated === 0) {
        // The rule expects a free unit but Shopify applied none (discount inactive, removed or limited): say nothing.
        el.dataset.bfMismatch = 'true';
        console.warn(`[BF] promo mismatch on line ${line.id}: expected ${free} free, Shopify allocated 0`);
      }
    });
    const estimate = section.querySelector('[data-bf-estimate]');
    if (estimate) {
      const show = result.totalSavings > 0 && !t.live;
      estimate.textContent = show ? t.estimate.replace('%amount%', formatMoney(result.totalSavings, t.moneyFormat)) : '';
      estimate.hidden = !show;
    }
  };

  const announceCount = (count) => {
    if (count === cartState.count) return;
    cartState.count = count;
    window.BF?.announceCart?.(count);
  };

  // Draws the quantities the customer asked for on top of the last Shopify-rendered cart.
  const applyLocal = () => {
    const section = cartRoot();
    if (!section) return;
    const { lines, t } = cartData(section);
    const local = localLines(lines);
    const pending = cartState.desired.size > 0;
    section.setAttribute('aria-busy', String(pending));
    const state = section.querySelector('[data-bf-cart-state]');
    if (state) state.hidden = !pending;

    local.forEach((l) => {
      const row = section.querySelector(`[data-bf-line="${CSS.escape(l.id)}"]`);
      if (!row) return;
      const input = row.querySelector('[data-bf-line-qty]');
      if (input && !(document.activeElement === input && pending)) input.value = String(l.quantity);
      if (cartState.desired.has(l.id)) row.dataset.bfPending = l.quantity === 0 ? 'remove' : 'true';
      else delete row.dataset.bfPending;
      const price = row.querySelector('[data-bf-line-price]');
      if (t.optimistic && price) price.textContent = formatMoney(l.price * l.quantity, t.moneyFormat);
    });

    if (t.optimistic) {
      const sum = formatMoney(local.reduce((a, l) => a + l.price * l.quantity, 0), t.moneyFormat);
      const subtotal = section.querySelector('[data-bf-subtotal]');
      const total = section.querySelector('[data-testid="bf-cart-total"]');
      if (subtotal) subtotal.textContent = sum;
      if (total) total.textContent = sum;
    }

    // Offer progress per bucket, with the same wording as the server render.
    const counts = new Map();
    local.forEach((l) => counts.set(bucketOf(l), (counts.get(bucketOf(l)) || 0) + l.quantity));
    section.querySelectorAll('[data-bf-bucket]').forEach((box) => {
      const key = box.dataset.bfBucket;
      if (key === 'none') return;
      const count = counts.get(key) || 0;
      const rem = count % 3;
      const filled = count > 0 && rem === 0 ? 3 : rem;
      box.querySelectorAll('[data-bf-progress] span').forEach((s, i) => { s.dataset.on = String(i < filled); });
      box.querySelector('[data-bf-progress]')?.setAttribute('aria-label', fill(t.progressAria, { count }));
      const status = box.querySelector('.bf-cart__group-status');
      if (status) status.textContent = count === 0 ? '' : rem === 0 ? fill(t.groupComplete, { count, free: count / 3 }) : fill(t.statusPartial, { count, need: 3 - rem });
    });
    const itemCount = local.reduce((a, l) => a + l.quantity, 0);
    const summary = section.querySelector('[data-testid="bf-cart-summary-line"]');
    if (summary && itemCount > 0) {
      const groups = new Set(local.filter((l) => l.quantity > 0 && bucketOf(l) !== 'none').map(bucketOf)).size;
      summary.textContent = fill(t.summary, { count: itemCount, groups });
    }
    markLines(local);
    announceCount(itemCount);
  };

  // Replaces the section with Shopify's render and puts keyboard focus back on the same control.
  const render = (html) => {
    const section = cartRoot();
    const fresh = html && new DOMParser().parseFromString(html, 'text/html').querySelector('[data-bf-cart-section]');
    if (!section || !fresh) return false;
    const active = document.activeElement;
    let focus = null;
    if (active && section.contains(active)) {
      const row = active.closest('[data-bf-line]');
      focus = {
        variant: row?.dataset.bfVariant,
        control: active.dataset.bfQtyStep ? `[data-bf-qty-step="${active.dataset.bfQtyStep}"]` : active.matches('[data-bf-line-qty]') ? '[data-bf-line-qty]' : null,
      };
    }
    section.replaceWith(fresh);
    if (focus) {
      const row = focus.variant && fresh.querySelector(`[data-bf-variant="${CSS.escape(focus.variant)}"]`);
      const target = (row && focus.control && row.querySelector(focus.control)) || fresh.querySelector('.bf-cart__title');
      target?.focus({ preventScroll: true });
    }
    markLines();
    return true;
  };

  const showError = (text) => {
    const section = cartRoot();
    const box = section?.querySelector('[data-bf-cart-error]');
    if (box) { box.textContent = text; box.hidden = false; }
    const live = section?.querySelector('[data-bf-cart-live]');
    if (live) live.textContent = text;
  };

  // Shopify can give a line a new key (for example when a discount starts to apply): carry waiting changes over by variant.
  const remapKeys = (items, lines) => {
    [...cartState.desired.keys()].forEach((key) => {
      if (items.some((i) => i.key === key)) return;
      const quantity = cartState.desired.get(key);
      cartState.desired.delete(key);
      const variantId = lines.find((l) => l.id === key)?.variantId;
      const matches = items.filter((i) => i.variant_id === variantId);
      if (matches.length === 1) cartState.desired.set(matches[0].key, quantity);
    });
  };

  const flush = async () => {
    clearTimeout(cartState.timer);
    const section = cartRoot();
    if (!section || cartState.inFlight || !cartState.desired.size) return;
    cartState.inFlight = true;
    const sent = new Map(cartState.desired);
    const id = sectionIdOf(section);
    const { lines, t } = cartData(section);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: Object.fromEntries(sent), sections: [id], sections_url: `${root}cart` }),
        signal: controller.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !Array.isArray(data.items)) throw new Error(data.description || data.message || `HTTP ${response.status}`);
      // Forget the changes this response confirmed; keep newer ones for the next request.
      sent.forEach((quantity, key) => { if (cartState.desired.get(key) === quantity) cartState.desired.delete(key); });
      remapKeys(data.items, lines);
      if (cartState.desired.size) return; // a newer change is waiting: do not draw this older state
      if (!render(data.sections?.[id])) throw new Error('section render missing');
      cartState.count = null;
      announceCount(data.item_count);
      // Discount allocation can split one variant into paid/free lines. Compare total quantities by variant.
      const requestedTotals = new Map();
      lines.forEach((line) => requestedTotals.set(line.variantId,
        (requestedTotals.get(line.variantId) || 0) + (sent.has(line.id) ? sent.get(line.id) : line.quantity)));
      const changedVariants = new Set([...sent.keys()].map((key) => lines.find((line) => line.id === key)?.variantId));
      const capped = [...changedVariants].map((variantId) => {
        const requested = requestedTotals.get(variantId);
        const actual = data.items.filter((item) => item.variant_id === variantId).reduce((total, item) => total + item.quantity, 0);
        return requested > 0 && actual < requested ? actual : null;
      }).find((quantity) => quantity !== null);
      if (capped != null) showError(fill(t.capped, { count: capped }));
      else announce(t.updated || '');
    } catch (error) {
      console.warn('[BF] cart update failed', error);
      // Roll back the failed request, but retain changes made while it was pending.
      sent.forEach((quantity, key) => { if (cartState.desired.get(key) === quantity) cartState.desired.delete(key); });
      let restored = false;
      try {
        const response = await fetch(`${root}cart?sections=${encodeURIComponent(id)}`);
        if (response.ok) restored = render((await response.json())[id]);
      } catch { /* offline: fall back to the last confirmed cart below */ }
      if (restored) {
        cartState.count = null;
        const confirmed = cartData(cartRoot()).lines.reduce((a, l) => a + l.quantity, 0);
        announceCount(confirmed);
        if (cartState.desired.size) applyLocal();
      } else {
        applyLocal(); // quantities and totals back to the last confirmed values
      }
      showError(t.error || '');
    } finally {
      clearTimeout(timeout);
      cartState.inFlight = false;
      if (cartState.desired.size) flush();
      else window.BF?.refresh?.();
    }
  };

  const setQuantity = (key, quantity) => {
    const section = cartRoot();
    if (!section) return;
    const line = cartData(section).lines.find((l) => l.id === key);
    if (!line) return;
    quantity = Math.min(99, Math.max(0, quantity));
    window.BF?.invalidatePromo?.();
    const error = section.querySelector('[data-bf-cart-error]');
    if (error) error.hidden = true;
    if (quantity === line.quantity && !cartState.inFlight) cartState.desired.delete(key);
    else cartState.desired.set(key, quantity);
    applyLocal();
    if (!cartState.desired.size || cartState.inFlight) return; // sent after the current request
    if (!cartState.timer) cartState.since = Date.now();
    clearTimeout(cartState.timer);
    const wait = Math.max(0, Math.min(COALESCE_MS, MAX_WAIT_MS - (Date.now() - cartState.since)));
    cartState.timer = setTimeout(() => { cartState.timer = 0; flush(); }, wait);
  };

  const shownQuantity = (key) => {
    const section = cartRoot();
    const line = cartData(section).lines.find((l) => l.id === key);
    return cartState.desired.has(key) ? cartState.desired.get(key) : line?.quantity ?? 0;
  };

  document.addEventListener('click', (event) => {
    const step = event.target.closest?.('[data-bf-cart-section] [data-bf-qty-step]');
    if (!step) return;
    const key = step.closest('[data-bf-line]')?.dataset.bfLine;
    if (key) setQuantity(key, shownQuantity(key) + Number(step.dataset.bfQtyStep));
  });
  document.addEventListener('click', (event) => {
    const remove = event.target.closest?.('[data-bf-cart-section] [data-bf-remove]');
    if (!remove) return;
    event.preventDefault();
    setQuantity(remove.dataset.bfRemove, 0);
  });
  // Typed quantities apply when the field is left or Enter is pressed (Enter would otherwise submit the form to checkout).
  const commitTyped = (input) => {
    const value = parseInt(input.value, 10);
    if (Number.isNaN(value)) { input.value = String(shownQuantity(input.dataset.bfLineQty)); return; }
    setQuantity(input.dataset.bfLineQty, value);
  };
  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('[data-bf-cart-section] [data-bf-line-qty]');
    if (input) commitTyped(input);
  });
  document.addEventListener('keydown', (event) => {
    const input = event.target.closest?.('[data-bf-cart-section] [data-bf-line-qty]');
    if (!input || event.key !== 'Enter') return;
    event.preventDefault();
    commitTyped(input);
  });

  const init = () => {
    cartState.count = cartData(cartRoot()).lines.reduce((a, l) => a + l.quantity, 0);
    if (cartState.desired.size) applyLocal(); else markLines();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('load', () => (cartState.desired.size ? applyLocal() : markLines()));
})();
